import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { FaWalking, FaBookmark } from 'react-icons/fa';
import hotelIcon from "../../../helper/svg/hotel_Icon.svg"
import restaurantIcon from "../../../helper/svg/rest.svg"
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  APIProvider,
  Map,
  useMap,
  Marker,
  InfoWindow,
} from '@vis.gl/react-google-maps';
import { MdStar, MdStarHalf, MdStarOutline } from "react-icons/md";
import { useNavigate } from 'react-router-dom';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const ClusteredMarkers = ({ locations, userLocation }) => {
  const [markers, setMarkers] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [directions, setDirections] = useState(null);
  const [directionSteps, setDirectionSteps] = useState([]);
  const map = useMap();
  const navigate = useNavigate()
  

  const clusterer = useMemo(() => {
    if (!map) return null;
    return new MarkerClusterer({ map });
  }, [map]);

  useEffect(() => {
    if (!map || !clusterer) return;

    const newMarkers = {};
    const markerInstances = locations.map((loc) => {
      const [lng, lat] = loc.location.coordinates;

      const iconUrl = loc.type === 'hotel' ? hotelIcon : restaurantIcon;

      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        icon: {
          url: iconUrl,
          scaledSize: new window.google.maps.Size(40, 40),
        },
      });

      marker.addListener('click', () => setActiveId(loc._id));
      newMarkers[loc._id] = marker;
      return marker;
    });

    setMarkers(newMarkers);
    clusterer.clearMarkers();
    clusterer.addMarkers(markerInstances);

    return () => {
      markerInstances.forEach((marker) => marker.setMap(null));
      clusterer.clearMarkers();
    };
  }, [locations, map, clusterer]);



  const activeLocation = locations.find((l) => l._id === activeId);
  const anchor = markers[activeId];

  const handleWalkClick = (location) => {
    if (!userLocation) return;
    const origin = `${userLocation.lat},${userLocation.lng}`;
    const destination = `${location.location.coordinates[1]},${location.location.coordinates[0]}`;
    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=walking`;
    window.open(url, "_blank");
  };

  const handleNavigate = (activeLocation) => {
    if (activeLocation.type === "hotel") {
      navigate(`/hoteldetail`, {
        state: { hotelId: activeLocation._id },
      });
    } else {
      navigate(`/restaurantdetail`, {
        state: { restaurantId: activeLocation._id  },
      });
    }
  }


  return (
    <>
      {activeLocation && anchor && (
        <InfoWindow anchor={anchor} onCloseClick={() => setActiveId(null)}>
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden w-80 max-w-sm">
            {/* Header with image */}
            <div className="relative">
              <img
                src={activeLocation.images?.[0]
                  ? `${activeLocation.images[0]}`
                  : 'https://via.placeholder.com/320x180/4CAF50/ffffff?text=No+Image'}
                alt={activeLocation.name}
                className="w-full h-48 object-cover"
              />
              <div className="absolute top-3 right-3">
                <div className="bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                  <MdStar size={16} className="text-[#FF9D00]" />
                  <span className="text-sm font-semibold text-gray-800">
                    {activeLocation?.rating?.toFixed(1) || 'N/A'}
                  </span>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <h3 className="text-xl font-bold text-white mb-1 line-clamp-2">
                  {activeLocation.name}
                </h3>
                <p className="text-white/90 text-sm line-clamp-1">
                  {activeLocation.address}
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              {/* Rating section */}
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center">
                  {Array(Math.floor(activeLocation?.rating || 0)).fill().map((_, index) => (
                    <MdStar key={`full-${index}`} size={18} className="text-[#FF9D00]" />
                  ))}
                  {activeLocation?.rating % 1 !== 0 && (
                    <MdStarHalf size={18} className="text-[#FF9D00]" />
                  )}
                  {Array(5 - Math.ceil(activeLocation?.rating || 0)).fill().map((_, index) => (
                    <MdStarOutline key={`empty-${index}`} size={18} className="text-[#FF9D00]" />
                  ))}
                </div>
                <span className="text-sm text-gray-600">
                  ({activeLocation?.rating?.toFixed(1) || 'N/A'})
                </span>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <button 
                  onClick={() => handleWalkClick(activeLocation)} 
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-600 px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium"
                >
                  <FaWalking size={16} />
                  <span>Directions</span>
                </button>
                <button 
                  onClick={() => handleNavigate(activeLocation)} 
                  className="flex-1 bg-[#4CAF50] hover:bg-[#45a049] text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium"
                >
                  View Details
                </button>
              </div>

              {/* Type indicator */}
              <div className="mt-3 flex items-center justify-center">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                  activeLocation.type === 'hotel' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {activeLocation.type === 'hotel' ? '🏨 Hotel' : '🍽️ Restaurant'}
                </span>
              </div>
            </div>
          </div>
        </InfoWindow>
      )}
    </>
  );
};

function Location({data : locations}) {
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
        },
        (err) => {
          console.warn(`Geolocation error: ${err.message}`);
        }
      );
    }
  }, []);

  // Get center from first location if available
  const getMapCenter = () => {
    if (locations && locations.length > 0 && locations[0]?.location?.coordinates) {
      const [lng, lat] = locations[0].location.coordinates;
      return { lat, lng };
    }
    return userLocation || { lat: 22.54992, lng: 0 };
  };

  // Determine zoom level based on whether we have a specific location
  const getZoomLevel = () => {
    if (locations && locations.length > 0 && locations[0]?.location?.coordinates) {
      return 15; // Close zoom for specific location
    }
    return 3; // Wide zoom for default view
  };

  if (!API_KEY) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="text-center p-4">
          <p className="text-gray-600">Google Maps API key is not configured.</p>
          <p className="text-sm text-gray-500 mt-2">Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment variables.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <APIProvider apiKey={API_KEY}>
        <Map
          defaultCenter={getMapCenter()}
          defaultZoom={getZoomLevel()}
          gestureHandling="greedy"
          disableDefaultUI
          style={{ width: '100%', height: '100%' }}
        >

          {userLocation && (
            <Marker
              position={userLocation}
              title="Your Location"
              icon={{
                url: 'data:image/svg+xml;charset=UTF-8,' +
                  encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
              <circle cx="12" cy="12" r="10" fill="#4285F4" stroke="white" stroke-width="4" />
            </svg>
          `),
                scaledSize: { width: 24, height: 24 },
              }}
            />
          )}


          <ClusteredMarkers locations={locations} userLocation={userLocation} />

        </Map>
      </APIProvider>
    </div>
  );
}

export default Location;
